import {
  syncDataLoaderFeature,
  dragAndDropFeature,
  ItemInstance,
  DragTarget,
  TreeInstance,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Datum,
  DatumPayload,
  Filetree,
  LoadedEntity,
  LoaderFn,
} from "./datastructure";
import { fetchFiles, RenderableData } from "./fetchFiles";
import { FiletreeRenderer } from "./rendering";
import {
  getContextMenuActions,
  handleContextMenuAction,
  createActionContext,
  moveItems,
  openFile,
  createFolder,
} from "./actions";
import { getCutItems, onCutItemsChange, CutItem } from "./actions/cut";
import { isChattableFileStatus } from "./actions/chatWithDocuments";
import { ConfirmationOptions } from "./actions/types";
import { useSortProgress } from "./sortProgress";
import { ContextMenuAction } from "./contextmenu";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { useSelector } from "react-redux";
import { useDrivePolicy } from "@/hooks/useDrivePolicy";
import { userIsSuperAdmin } from "@/components/FpAppV4/services/userIsSuperAdmin";
import React from "react";
import {
  useFileStatusUpdates,
  FileStatusUpdate,
} from "./upload/useFileStatusUpdates";
import {
  TreeLoadingWrapper,
  handleFileStatusUpdate,
  useSelection,
  createCanDropValidator,
} from "./features";
/** File click event data passed to onFileClick callback */
export interface FileClickEvent {
  fileId: string;
  fileName: string;
  fileType: string;
  filePath: string[];
}

interface UseFiletreeOptions {
  prefetchDepth?: number;
  /** If set, show contents of this specific drive. */
  driveId?: string;
  /** If true, show files across all drives with synthetic drive folders at the top level. Only works when driveId is undefined. */
  showAcrossDrives?: boolean;
  /** Whether to show checkboxes for selection */
  showCheckboxes?: boolean;
  /**
   * Whether still-processing (greyed-out) files may be selected. The Documents
   * page opts in for bulk move/delete/open (#10770); the query/report "Select
   * Documents" modals leave it false so unprocessed files stay unselectable
   * (#9880). Greying is unaffected. Defaults to false.
   */
  allowProcessingFileSelection?: boolean;
  /** Sort column */
  sortBy: "name" | "date";
  /** Sort direction */
  sortDirection: "asc" | "desc";
  /** Callback when sort changes */
  onSortChange: (column: "name" | "date") => void;
  /** Optional callback when a file is clicked. If not provided, opens file in viewer. */
  onFileClick?: (event: FileClickEvent) => void;
  /** Callback for bulk-deleting all selected items (called when delete is triggered on a multi-selection) */
  onBulkDelete?: () => Promise<void>;
  /** Callback for bulk-cutting all selected items (called when cut is triggered on a multi-selection) */
  onBulkCut?: () => void;
  /** Callback for bulk-chatting with all selected items (called when chat is triggered on a multi-selection) */
  onBulkChatWithDocuments?: () => Promise<void>;
  /** Optional blob kinds to exclude from file listings (e.g. interaction docs, emails) */
  excludeBlobKinds?: string[];
  /** Narrow-frame date treatment — see FiletreeRenderer.narrowDate (#11742). */
  narrowDate?: "compact" | "hidden";
  /**
   * Whether the tree may load its contents. False keeps it empty and issues
   * no request, for a host that mounts before it is shown (a closed picker,
   * #12239); flipping it to true loads the root. Defaults to true.
   */
  loadTree?: boolean;
}

export const useFiletreeCore = ({
  prefetchDepth = 2,
  driveId,
  showAcrossDrives = false,
  showCheckboxes = false,
  allowProcessingFileSelection = false,
  sortBy,
  sortDirection,
  onSortChange,
  onFileClick,
  onBulkDelete,
  onBulkCut,
  onBulkChatWithDocuments,
  excludeBlobKinds,
  narrowDate,
  loadTree = true,
}: UseFiletreeOptions) => {
  const isAcrossDrivesMode = showAcrossDrives && driveId === undefined;
  // Which drives are read-only is resolved per-org by the backend (#10972).
  const drivePolicy = useDrivePolicy();
  const driveNames = useMemo(
    () => ({
      readOnly: drivePolicy.read_only_drive_names,
      sftp: drivePolicy.sftp_drive_names,
      sharepointRoots: drivePolicy.sharepoint_root_names ?? [],
    }),
    [drivePolicy]
  );
  const isAdmin = useSelector((state: any) => userIsSuperAdmin(state.userData));

  const loaderFn: LoaderFn = async (datum: Datum) => {
    // --- ACROSS DRIVES MODE, ROOT LOAD (special case) ---
    // This is the only truly special case: we fetch all drives and their contents,
    // create synthetic drive folders, and prefix each entity with its drive name.
    if (isAcrossDrivesMode && datum.path.length === 0) {
      const response = await fetchFiles({
        maxDepth: prefetchDepth,
        // Folder structure prefetches to prefetchDepth, file rows only one
        // level deep (#11322). When drives are enabled the rendered level is
        // the synthetic drive folders, so depth-1 files are one level below
        // it — but drivesEnabled isn't known until this response arrives, and
        // when it's off the depth-1 files ARE the rendered root files, so one
        // request has to serve both. A drive's root files are a bounded set,
        // unlike the unbounded folder contents this parameter exists to stop.
        fileMaxDepth: 1,
        excludeBlobKinds,
      });

      // Store drives info in filetree for consumers to access
      filetree.drives = response.drives_listing.drives;
      filetree.drivesEnabled = response.drives_listing.drivesEnabled;

      const entities: LoadedEntity[] = [];

      // If drives not enabled, load files directly without drive prefix
      if (!response.drives_listing.drivesEnabled) {
        // Add folders without drive prefix
        for (const folder of response.folders) {
          entities.push({
            path: folder.filePath,
            type: "folder",
            payload: {
              type: "folder",
              documentDate: folder.documentDate,
              driveId: null,
            } satisfies RenderableData,
          });
        }
        // Add files without drive prefix
        for (const file of response.fileList) {
          entities.push({
            path: [...file.filePath, file.fileName],
            type: "file",
            payload: {
              type: "file",
              fileId: file.fileId,
              fileName: file.fileName,
              fileType: file.fileType,
              uploadedAt: file.uploadedAt,
              documentDate: file.documentDate,
              status: file.status,
              isBlob: file.isBlob,
              isPasswordProtected: file.isPasswordProtected,
              hasStoredPassword: file.hasStoredPassword,
              taskId: file.taskId,
              driveId: null,
            } satisfies RenderableData,
          });
        }
        return entities;
      }

      // Drives enabled: Build drive info lookup (use driveId as path key for uniqueness)
      const drives = response.drives_listing.drives;
      const driveIdToInfo = new Map(
        drives.map((d) => [d._id, { id: d._id, name: d.name }])
      );

      // Add synthetic drive folders (path uses driveId for uniqueness)
      for (const drive of drives) {
        entities.push({
          path: [drive._id],
          type: "folder",
          payload: {
            type: "folder",
            documentDate: "",
            driveId: drive._id,
            driveName: drive.name,
          } satisfies DatumPayload,
        });
      }

      // Add folders (prefixed with driveId for uniqueness)
      for (const folder of response.folders) {
        const driveInfo = driveIdToInfo.get(folder.driveId ?? "");
        if (!driveInfo) continue;
        entities.push({
          path: [driveInfo.id, ...folder.filePath],
          type: "folder",
          payload: {
            type: "folder",
            documentDate: folder.documentDate,
            driveId: folder.driveId,
          } satisfies RenderableData,
        });
      }

      // Add files (prefixed with driveId for uniqueness)
      for (const file of response.fileList) {
        const driveInfo = driveIdToInfo.get(file.driveId ?? "");
        if (!driveInfo) continue;
        entities.push({
          path: [driveInfo.id, ...file.filePath, file.fileName],
          type: "file",
          payload: {
            type: "file",
            fileId: file.fileId,
            fileName: file.fileName,
            fileType: file.fileType,
            uploadedAt: file.uploadedAt,
            documentDate: file.documentDate,
            status: file.status,
            isBlob: file.isBlob,
            isPasswordProtected: file.isPasswordProtected,
            hasStoredPassword: file.hasStoredPassword,
            taskId: file.taskId,
            driveId: file.driveId,
          } satisfies RenderableData,
        });
      }

      return entities;
    }

    // --- NON-ROOT LOAD (unified for both modes) ---
    // Get driveId directly from the datum's payload
    const effectiveDriveId = datum.payload?.driveId ?? driveId;

    // In across-drives mode with drives enabled, first path segment is the driveId - strip it for the API.
    // In single-drive mode or when drives are disabled, the path is already the real filesystem path.
    const apiPath = filetree.shouldStripDrivePrefix
      ? datum.path.slice(1)
      : datum.path;

    // Path prefix for returned entities (to reconstruct full tree paths using driveId)
    const pathPrefix = filetree.shouldStripDrivePrefix
      ? [effectiveDriveId ?? datum.path[0]]
      : [];

    const response = await fetchFiles({
      pathPrefix: apiPath,
      maxDepth: prefetchDepth,
      // See the root load above: files only at the rendered level (#11322).
      fileMaxDepth: 1,
      driveId: effectiveDriveId,
      excludeBlobKinds,
    });

    // Store drives info on root load (single-drive mode)
    if (datum.path.length === 0 && response.drives_listing) {
      filetree.drives = response.drives_listing.drives;
      filetree.drivesEnabled = response.drives_listing.drivesEnabled;
    }

    return [
      ...response.folders.map((folder) => ({
        path: [...pathPrefix, ...folder.filePath],
        type: "folder" as const,
        payload: {
          type: "folder",
          documentDate: folder.documentDate,
          driveId: folder.driveId,
        } satisfies RenderableData,
      })),
      ...response.fileList.map((file) => ({
        path: [...pathPrefix, ...file.filePath, file.fileName],
        type: "file" as const,
        payload: {
          type: "file",
          fileId: file.fileId,
          fileName: file.fileName,
          fileType: file.fileType,
          uploadedAt: file.uploadedAt,
          documentDate: file.documentDate,
          status: file.status,
          isBlob: file.isBlob,
          isPasswordProtected: file.isPasswordProtected,
          hasStoredPassword: file.hasStoredPassword,
          taskId: file.taskId,
          driveId: file.driveId,
        } satisfies RenderableData,
      })),
    ];
  };

  const loader = useRef<LoaderFn>(loaderFn);
  loader.current = loaderFn;

  // Ref to hold the tree instance so onRebuild can invalidate its cache on initial load
  const treeRef = useRef<TreeInstance<Datum> | null>(null);
  // Track whether initial root load has completed (to avoid unnecessary cache invalidation)
  const hasInitiallyLoadedRef = useRef(false);

  const [_, setRerender] = useState(0);

  // Helper to create and configure a Filetree instance
  const createConfiguredFiletree = () => {
    const ft = new Filetree();
    ft.isAcrossDrivesMode = isAcrossDrivesMode;
    ft.readOnlyDriveNames = driveNames.readOnly;
    ft.sftpDriveNames = driveNames.sftp;
    ft.sharepointRootNames = driveNames.sharepointRoots;
    ft.sortBy = sortBy;
    ft.sortDirection = sortDirection;
    // In single-drive mode, set root's driveId so drag-drop works correctly
    if (!isAcrossDrivesMode && driveId) {
      ft.root.payload = { type: "folder", documentDate: "", driveId };
    }
    ft.onRebuild = (datum) => {
      // On initial root load, invalidate the tree's cache so it re-queries getChildren.
      // This is needed because the tree caches the empty [] from the first query
      // before data was loaded. Subsequent operations don't need this.
      if (
        treeRef.current &&
        !hasInitiallyLoadedRef.current &&
        datum.path.length === 0
      ) {
        hasInitiallyLoadedRef.current = true;
      }
      setRerender((n) => n + 1);
    };
    return ft;
  };

  const [filetree, setFiletree] = useState(createConfiguredFiletree);

  // Selection feature - handles all selection state and logic
  const {
    selection,
    isItemSelected,
    isItemInSelectedContext,
    toggleItemSelection,
    clearSelection,
    selectFiles,
    selectFolders,
    setSelection,
    resetSelection,
    updatePendingMetadata,
  } = useSelection({
    filetree,
    isAcrossDrivesMode,
    allowProcessingFileSelection,
  });

  useEffect(() => {
    hasInitiallyLoadedRef.current = false;
    setFiletree(createConfiguredFiletree());
    resetSelection();
  }, [driveId, showAcrossDrives, isAcrossDrivesMode, resetSelection]);

  // Update filetree sort settings when they change
  useEffect(() => {
    filetree.setSort(sortBy, sortDirection);
  }, [filetree, sortBy, sortDirection]);

  // The drive policy resolves asynchronously, so the filetree is usually built
  // before the names arrive. Re-apply them and re-sort, since read-only client
  // drives pin to the top (#10972).
  useEffect(() => {
    filetree.readOnlyDriveNames = driveNames.readOnly;
    filetree.sftpDriveNames = driveNames.sftp;
    filetree.sharepointRootNames = driveNames.sharepointRoots;
    filetree.setSort(sortBy, sortDirection);
  }, [filetree, driveNames, sortBy, sortDirection]);

  // Drag and drop validation - prevents cross-drive moves and dropping into self
  const canDrop = useMemo(
    () =>
      createCanDropValidator({
        isAcrossDrivesMode,
        driveId,
        readOnlyDriveNames: driveNames.readOnly,
        sharepointRootNames: driveNames.sharepointRoots,
      }),
    [isAcrossDrivesMode, driveId, driveNames]
  );

  // Scopes read-only client drives for sort/icon/tooltip. Stable identity keeps
  // the memoized FileTreeItem rows from re-rendering.
  const readOnlyCtx = useMemo(
    () => ({
      readOnlyDriveNames: driveNames.readOnly,
      sftpDriveNames: driveNames.sftp,
      driveConnectors: drivePolicy.drive_connectors,
      sharepointRootNames: driveNames.sharepointRoots,
      isAcrossDrivesMode,
    }),
    [driveNames, drivePolicy, isAcrossDrivesMode]
  );

  // Force re-render function for refreshing after actions
  const [, forceUpdate] = useState(0);
  const refreshTree = useCallback(() => {
    // Clear the filetree loaded state to force a refetch
    forceUpdate((n) => n + 1);
  }, []);

  // Confirmation modal state
  const [confirmationState, setConfirmationState] = useState<{
    open: boolean;
    options: ConfirmationOptions;
    resolve: (value: boolean) => void;
  }>({
    open: false,
    options: { title: "", message: "" },
    resolve: () => {},
  });

  const showConfirmation = useCallback(
    (options: ConfirmationOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setConfirmationState({ open: true, options, resolve });
      });
    },
    []
  );

  // Async sort progress: the sort action enqueues the task and hands us the id;
  // this drives the persistent "Sorting X of N…" banner and polls to completion.
  const { onSortStarted, sortBanner } = useSortProgress(refreshTree);

  // Create action context for context menu actions
  const actionContext = createActionContext({
    refreshTree,
    driveId,
    readOnlyDriveNames: driveNames.readOnly,
    sftpDriveNames: driveNames.sftp,
    sharepointRootNames: driveNames.sharepointRoots,
    isAdmin,
    filetree,
    isAcrossDrivesMode,
    showConfirmation,
    onSortStarted,
  });

  // Handle item click - open files when clicked (or call custom handler)
  const handleItemClick = useCallback(
    (itemId: string) => {
      const datum = filetree.lookup.get(itemId);
      if (datum?.type === "file" && datum.payload?.type === "file") {
        if (onFileClick) {
          // Use custom callback
          onFileClick({
            fileId: datum.payload.fileId || "",
            fileName: datum.payload.fileName || datum.name || "",
            fileType: datum.payload.fileType || "",
            filePath: datum.path,
          });
        } else {
          // Default: open file in viewer
          openFile(datum, actionContext);
        }
      }
    },
    [filetree, actionContext, onFileClick]
  );

  // Handle drop - move files to target location
  // When a selected item is dragged, merge all selected items into the move
  const onDrop = useCallback(
    (items: ItemInstance<Datum>[], target: DragTarget<Datum>) => {
      const targetDatum = target.item.getItemData();
      let itemDatums = items.map((i) => i.getItemData());

      // If any dragged item is selected, include all selected datums in the move.
      // Skip descendants of selected folders — moving a folder moves its contents automatically.
      const isDraggingSelected = itemDatums.some((d) => isItemSelected(d.id));
      if (isDraggingSelected) {
        const draggedIds = new Set(itemDatums.map((d) => d.id));
        const selectedDatums = filetree.datums.filter(
          (d) =>
            d.path.length > 0 && isItemSelected(d.id) && !draggedIds.has(d.id)
        );
        // Filter out items whose ancestor is already in the move set
        const allMovingIds = new Set([
          ...draggedIds,
          ...selectedDatums.map((d) => d.id),
        ]);
        const topLevelDatums = selectedDatums.filter((d) => {
          let ancestor = d.parent;
          while (ancestor && ancestor.path.length > 0) {
            if (allMovingIds.has(ancestor.id)) return false;
            ancestor = ancestor.parent;
          }
          return true;
        });
        itemDatums = [...itemDatums, ...topLevelDatums];
      }

      moveItems({
        items: itemDatums,
        target: targetDatum,
        filetree,
        context: actionContext,
      });

      if (isDraggingSelected) {
        clearSelection();
      }
    },
    [filetree, actionContext, isItemSelected, clearSelection]
  );

  const tree = useTree<Datum>({
    rootItemId: filetree.root.id,
    getItemName: (item) => item.getItemData().name || "Root Datum",
    isItemFolder: (item) => item.getItemData().type === "folder",
    dataLoader: {
      getItem(itemId) {
        const datum = filetree.lookup.get(itemId);
        // Return root as fallback if datum not found (can happen briefly after rename)
        // The tree will re-render with correct data on next cycle
        if (!datum) return filetree.root;
        return datum;
      },
      getChildren(itemId) {
        if (!loadTree) return [];
        const datum = filetree.lookup.get(itemId);
        if (!datum) return [];

        const contents = filetree.contents(datum, loader.current);
        if (contents.type === "loading") return [];
        return contents.children.map((child) => child.id);
      },
    },
    features: [syncDataLoaderFeature, dragAndDropFeature],
    // Drag and drop config
    canReorder: false,
    canDrop,
    onDrop,
  });

  // Store tree ref so onRebuild can invalidate its cache
  treeRef.current = tree;

  // The tree reads its root on mount; one mounted with loading held off read
  // nothing then, so it reads the root when loading is let through (#12239).
  const loadedOnMountRef = useRef(loadTree);
  useEffect(() => {
    if (!loadTree || loadedOnMountRef.current) return;
    loadedOnMountRef.current = true;
    tree.rebuildTree();
  }, [loadTree, tree]);

  // Custom loading detection that works with your Filetree implementation
  const isItemLoading = useCallback(
    (itemId: string): boolean => {
      const datum = filetree.lookup.get(itemId);
      if (!datum) return false;
      // Don't show spinner for folders we know are empty
      if (filetree.isKnownEmpty(datum)) return false;
      const item = tree.getItemInstance(itemId);
      return (
        filetree.isReloading(datum) &&
        datum.children === undefined &&
        item.isExpanded()
      );
    },
    [filetree, tree]
  );

  // Get context menu actions for a datum
  const getMenuActions = useCallback(
    (itemId: string, item: ItemInstance<Datum>): ContextMenuAction[] => {
      const datum = item.getItemData();
      const isMultiSelect = selection.totalCount > 1 && isItemSelected(itemId);
      // Bulk chat waits for every selected file to be chattable: a failed
      // file is (#13887), a file still processing is not.
      const selectedFileIds = new Set(selection.fileIds);
      const selectionHasProcessingFile = filetree.datums.some(
        (d) =>
          d.type === "file" &&
          d.payload?.type === "file" &&
          d.payload.fileId &&
          selectedFileIds.has(d.payload.fileId) &&
          !isChattableFileStatus(d.payload.status)
      );
      return getContextMenuActions(datum, {
        ...actionContext,
        isMultiSelect,
        hasBulkChat:
          Boolean(onBulkChatWithDocuments) && !selectionHasProcessingFile,
      });
    },
    [
      actionContext,
      selection.totalCount,
      selection.fileIds,
      isItemSelected,
      onBulkChatWithDocuments,
      filetree.datums,
    ]
  );

  // Handle context menu action execution
  const onMenuAction = useCallback(
    async (action: string, itemId: string) => {
      const datum = filetree.lookup.get(itemId);
      if (!datum) {
        console.error("onMenuAction: datum not found for itemId:", itemId);
        return;
      }

      // If the item is selected and there are multiple selections,
      // delegate to the bulk handlers
      if (isItemSelected(datum.id) && selection.totalCount > 1) {
        if (action === "delete" && onBulkDelete) {
          await onBulkDelete();
          return;
        }
        if (action === "cut" && onBulkCut) {
          onBulkCut();
          return;
        }
        if (
          (action === "chat-with-document" ||
            action === "chat-with-documents") &&
          onBulkChatWithDocuments
        ) {
          await onBulkChatWithDocuments();
          return;
        }
        if (action === "open-files") {
          for (const file of selection.files) {
            const url = new URL(window.location.href);
            url.searchParams.set("docId", file.id);
            window.open(url.toString(), "_blank");
          }
          return;
        }
      }

      await handleContextMenuAction(action, datum, actionContext);
    },
    [
      filetree,
      actionContext,
      isItemSelected,
      selection.totalCount,
      selection.files,
      onBulkDelete,
      onBulkCut,
      onBulkChatWithDocuments,
    ]
  );

  // Check if datum is pending a move (for styling)
  const isPendingMove = useCallback(
    (datum: Datum) => filetree.pendingMoveDatums.has(datum),
    [filetree]
  );

  // Handle WebSocket file status updates
  const handleFileStatusChange = useCallback(
    (update: FileStatusUpdate) => {
      // Through the tree, so a folder load already in flight replays it over
      // its older answer rather than wiping it (#12187).
      filetree.applyPushedUpdate(() =>
        handleFileStatusUpdate(filetree, update, isAcrossDrivesMode)
      );
    },
    [filetree, isAcrossDrivesMode]
  );

  // Subscribe to WebSocket file status updates
  useFileStatusUpdates(handleFileStatusChange);

  // Handler for creating a new folder at root level
  const createFolderAtRoot = useCallback(
    async (folderName: string) => {
      // In across-drives mode, we can't create at the synthetic root
      // User needs to select a specific drive first
      if (isAcrossDrivesMode) {
        actionContext.showToast?.(
          "Please select a specific drive to create a folder",
          "info"
        );
        return;
      }

      // Create folder at the root of the current drive
      await createFolder(filetree.root, folderName, actionContext);
    },
    [filetree, actionContext, isAcrossDrivesMode]
  );

  // Create drive ID to name map for tooltips
  const driveIdToName = useMemo(
    () => new Map(filetree.drives.map((d) => [d._id, d.name])),
    [filetree.drives]
  );

  // Track cut items for visual dimming
  const [cutDatumIds, setCutDatumIds] = useState<Set<string>>(
    () => new Set(getCutItems().map((c) => c.datumId))
  );
  useEffect(() => {
    return onCutItemsChange((items) =>
      setCutDatumIds(new Set(items.map((c) => c.datumId)))
    );
  }, []);

  // Check if a datum (or any ancestor) is cut
  const isCut = useCallback(
    (datum: Datum): boolean => {
      let current: Datum | undefined = datum;
      while (current) {
        if (cutDatumIds.has(current.id)) return true;
        current = current.parent;
      }
      return false;
    },
    [cutDatumIds]
  );

  const rendered = (
    <TreeLoadingWrapper filetree={filetree}>
      {sortBanner}
      <FiletreeRenderer
        key={showAcrossDrives ? "across-drives" : (driveId ?? "no-drive")}
        tree={tree}
        isItemLoading={isItemLoading}
        showCheckboxes={showCheckboxes}
        isItemSelected={isItemSelected}
        onCheckboxToggle={toggleItemSelection}
        allowProcessingFileSelection={allowProcessingFileSelection}
        getContextMenuActions={getMenuActions}
        onContextMenuAction={onMenuAction}
        onItemClick={handleItemClick}
        isPendingMove={isPendingMove}
        isCut={isCut}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
        driveIdToName={driveIdToName}
        readOnlyCtx={readOnlyCtx}
        narrowDate={narrowDate}
      />
      <ConfirmationModal
        open={confirmationState.open}
        onClose={() => {
          confirmationState.resolve(false);
          setConfirmationState((prev) => ({ ...prev, open: false }));
        }}
        onConfirm={() => {
          confirmationState.resolve(true);
          setConfirmationState((prev) => ({ ...prev, open: false }));
        }}
        title={confirmationState.options.title}
        message={confirmationState.options.message}
        confirmText={confirmationState.options.confirmText ?? "Sync"}
        cancelText={confirmationState.options.cancelText ?? "Cancel"}
      />
    </TreeLoadingWrapper>
  );

  // Expose removeDatums for optimistic bulk deletion
  const removeDatums = useCallback(
    (ids: Set<string>) => {
      filetree.removeDatums(ids);
    },
    [filetree]
  );

  return {
    tree: rendered,
    selection,
    clearSelection,
    selectFiles,
    selectFolders,
    setSelection,
    updatePendingMetadata,
    createFolderAtRoot,
    isItemSelected,
    isItemInSelectedContext,
    toggleItemSelection,
    removeDatums,
    // Admin flag, so surfaces built on this hook (search results) gate the
    // admin-only context-menu actions the same way the tree does.
    isAdmin,
    // Drive information (populated after root load)
    drives: filetree.drives,
    drivesEnabled: filetree.drivesEnabled,
    // All loaded datums (for filtering selected items)
    datums: filetree.datums,
  };
};
